import { useEffect, useState } from "react";
import type { FieldContent } from "@capture/shared";
import { useFormConfigStore, resolveV3, activeForm as resolveActiveForm } from "../../state/store";
import { FieldRow, blurOnEnter } from "./Field";

/**
 * Identity section — name, label, dataType.
 *
 * The dataType select exposes all 8 values from the WU-1 widened union.
 * Order is presentational — text-like first, then numeric, temporal,
 * boolean, list-like — to match the operator's mental model.
 */

const IDENTIFIER_RE = /^[A-Za-z][A-Za-z0-9_]*$/;

type DataType = FieldContent["dataType"];

const DATA_TYPES: { value: DataType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "decimal", label: "Decimal" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "dropdown", label: "Dropdown" },
  { value: "lookup", label: "Lookup" },
];

export function IdentitySection() {
  // Source of truth is `contentV3`. Mirror the store's `resolveV3` + `activeForm`
  // so the selected field is read from the ACTIVE form's slice (Spec A §5 — every
  // field is form-owned). `resolveV3` migrates legacy `content` on the fly for
  // test/pre-load paths; `activeForm` falls back to the default form for a
  // null/stale activeFormId — exactly matching the write path (`updateField` →
  // patchActiveForm).
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  const activeFormId = useFormConfigStore((s) => s.activeFormId);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const updateField = useFormConfigStore((s) => s.updateField);

  const v3 = resolveV3({ contentV3, content: legacyContent });
  const form = v3 ? resolveActiveForm(v3, activeFormId) : null;
  const field = form?.fields.find((f) => f.name === selectedFieldName) ?? null;

  const [nameDraft, setNameDraft] = useState(field?.name ?? "");
  const [labelDraft, setLabelDraft] = useState(field?.label ?? "");
  const [nameError, setNameError] = useState<string | null>(null);
  // R-141 — explicit human-readable label required; empty / whitespace /
  // legacy "New Field" placeholder are rejected so the drawer can't
  // silently produce a job type where every field reads "New Field" in
  // the Verification Station.
  const [labelError, setLabelError] = useState<string | null>(null);

  useEffect(() => {
    setNameDraft(field?.name ?? "");
    setLabelDraft(field?.label ?? "");
    setNameError(null);
    setLabelError(null);
  }, [field?.fieldDefinitionId, field?.name, field?.label]);

  if (!field || !form) return null;

  function commitName() {
    if (!field || !form) return;
    const next = nameDraft.trim();
    if (next === field.name) {
      setNameError(null);
      return;
    }
    if (!next) {
      setNameError("Field name is required.");
      setNameDraft(field.name);
      return;
    }
    if (!IDENTIFIER_RE.test(next)) {
      setNameError("Use letters, digits, and underscores only (must start with a letter).");
      setNameDraft(field.name);
      return;
    }
    const dup = form.fields.some(
      (f) => f.name === next && f.fieldDefinitionId !== field.fieldDefinitionId,
    );
    if (dup) {
      setNameError("A field with that name already exists.");
      setNameDraft(field.name);
      return;
    }
    setNameError(null);
    // F-010 — `updateField` now advances `selectedFieldName` to the new name
    // itself, preserving `fieldEditorOpen`. We must NOT call `setSelectedField`
    // here: it sets `fieldEditorOpen: false`, which unmounted the drawer the
    // moment the operator committed a rename (blur of the Name field).
    updateField(field.name, { name: next });
  }

  function commitLabel() {
    if (!field) return;
    const next = labelDraft.trim();
    // R-141 — reject empty + the "New Field" placeholder. Both produced
    // job types where every field read "New Field" in the operator UI.
    if (!next) {
      setLabelError("Label is required.");
      return;
    }
    if (next === "New Field") {
      setLabelError("Set a real label — \"New Field\" is the placeholder, not a name.");
      return;
    }
    setLabelError(null);
    if (next === field.label) return;
    updateField(field.name, { label: next });
    setLabelDraft(next);
  }

  function commitDataType(next: DataType) {
    if (!field || next === field.dataType) return;
    updateField(field.name, { dataType: next });
  }

  return (
    <section className="field-section" aria-label="Identity">
      <h3 className="field-section__title">Identity</h3>

      <FieldRow
        htmlFor="ident-name"
        label="Field name"
        hint="Used by OCR, exports, and rules. No spaces."
        error={nameError}
      >
        <input
          id="ident-name"
          type="text"
          className="field-section__input"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") {
              setNameDraft(field.name);
              setNameError(null);
              (e.target as HTMLInputElement).blur();
            }
          }}
        />
      </FieldRow>

      <FieldRow
        htmlFor="ident-label"
        label="Label"
        hint="What operators see on screen."
        error={labelError}
      >
        <input
          id="ident-label"
          type="text"
          className="field-section__input"
          value={labelDraft}
          onChange={(e) => setLabelDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={blurOnEnter}
          aria-invalid={labelError !== null}
        />
      </FieldRow>

      <FieldRow htmlFor="ident-type" label="Data type">
        <select
          id="ident-type"
          className="field-section__input"
          value={field.dataType}
          onChange={(e) => commitDataType(e.target.value as DataType)}
        >
          {DATA_TYPES.map((dt) => (
            <option key={dt.value} value={dt.value}>{dt.label}</option>
          ))}
        </select>
      </FieldRow>
    </section>
  );
}
